import { computed, inject, Injectable, signal } from '@angular/core';
import { LaximoTokensStateService } from '@services/api/internal/laximo-tokens-state.service';
import { catchError, EMPTY, filter } from 'rxjs';
import { LaximoTokensState } from '../../modules/catalog/laximo/models/laximo-tokens-state.interface';

/**
 * Client-side Laximo tokens state: loaded from `GET laximo-tokens/state`, the balance is then kept fresh
 * from the `X-Laximo-Tokens-Balance` response header and the block flag from `LaximoTokensExhausted` errors.
 * `isWarning`/`isBlocked` are computed by the server (no-block flag, internal partners), the client only mirrors them.
 */
@Injectable({
  providedIn: 'root',
})
export class LaximoTokensStoreService {
  private readonly api = inject(LaximoTokensStateService);
  private readonly stateSignal = signal<LaximoTokensState | null>(null);
  private warnShownReported = false;
  // Revisions let a `state` response detect local events that happened while it was in flight.
  private refreshRequestId = 0;
  private blockRevision = 0;
  private balanceRevision = 0;
  // Kept even before the first `state` response: a charged catalog response may arrive while it is in flight.
  private headerBalance: number | null = null;

  private applyServerState(state: LaximoTokensState, blockRevision: number, balanceRevision: number): void {
    this.stateSignal.update(current => {
      // An exhausted-tokens error received after the request started is the most recent server answer.
      if (blockRevision !== this.blockRevision) {
        return current;
      }

      // A balance header received after the request started is fresher than the snapshot balance.
      return balanceRevision !== this.balanceRevision && this.headerBalance !== null ? { ...state, balance: this.headerBalance } : state;
    });
  }

  readonly state = this.stateSignal.asReadonly();
  readonly isBlocked = computed(() => this.stateSignal()?.isBlocked ?? false);
  readonly isWarning = computed(() => !this.isBlocked() && (this.stateSignal()?.isWarning ?? false));

  refresh(): void {
    const requestId = ++this.refreshRequestId;
    const blockRevision = this.blockRevision;
    const balanceRevision = this.balanceRevision;

    this.api
      .getState()
      .pipe(
        filter(() => requestId === this.refreshRequestId),
        // A failed refresh keeps the last known state: dropping a confirmed block would re-enable the search.
        catchError(() => EMPTY),
      )
      .subscribe(state => this.applyServerState(state, blockRevision, balanceRevision));
  }

  // Only the balance follows the response header (TISS-7526): the warning and block flags come with `state` on the
  // catalog entry and from `LaximoTokensExhausted` errors.
  applyBalance(balance: number): void {
    this.balanceRevision++;
    this.headerBalance = balance;
    this.stateSignal.update(state => (state ? { ...state, balance } : state));
  }

  markBlocked(): void {
    this.blockRevision++;
    this.stateSignal.update(state => ({
      balance: state?.balance ?? 0,
      warnThreshold: state?.warnThreshold ?? 0,
      isWarning: false,
      isBlocked: true,
    }));
  }

  reportWarnShown(): void {
    if (this.warnShownReported) {
      return;
    }

    this.warnShownReported = true;
    this.api.markWarnShown().subscribe({
      // Allows the next banner render to report again after a failed call.
      error: () => (this.warnShownReported = false),
    });
  }
}
